import React from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '@/i18n/LanguageContext';
import Seo from '@/components/Seo';
import {
  breadcrumbSchema, faqSchema, platformFaqs, offerCatalogSchema, itemListSchema,
} from '@/lib/seoSchema';

import {
  Breadcrumbs, PageHero, AnswerBlock, Section, FaqSection, LinkGrid, CtaBand, PageWrap, EntityFacts, TrustStrip,
} from '@/components/seo/SeoBlocks';
import {
  ADDRESS_LINES, BRAND, LEAD_PRICE, money, PLAN_CODES, PLAN_PRICES, PLAN_INCLUDED_LEADS, PLAN_TERM_MONTHS,
  planBillingSuffix, brandDescription, TRUST_PAGES,
} from '@/data/constants';



import { SEO_TRADES } from '@/data/seo/trades';
import { COUNTIES } from '@/data/seo/locations';
import { RESOURCES } from '@/data/seo/resources';

const hubLinks = (path: (p: string) => string, t: (k: string) => string) => [
  { label: t('seo.tradesTitle'), to: path('/trades') },
  { label: 'California', to: path('/california') },
  { label: t('seo.leadsHub'), to: path('/leads') },
  { label: t('seo.resourcesTitle'), to: path('/resources') },
  { label: t('seo.projectDirTitle'), to: path('/project-directory') },
  { label: t('seo.contractorDirTitle'), to: path('/contractor-directory') },
];

/* ---------------------------------------------------------------- ABOUT */
export const AboutPage: React.FC = () => {
  const { lang, path, t } = useI18n();
  const faqs = platformFaqs(lang);
  return (
    <>
      <Seo
        title={lang === 'es' ? `Acerca de ${BRAND.name}` : `About ${BRAND.name}`}
        description={t('seo.aboutMeta')}
        schema={[
          breadcrumbSchema([{ name: 'Home', url: `/${lang}` }, { name: 'About', url: `/${lang}/about` }]),
          faqSchema(faqs),
        ]}

      />
      <PageHero eyebrow={t('seo.aboutEyebrow')} title={t('seo.aboutTitle')} intro={t('seo.aboutIntro')} />
      <PageWrap>
        <Breadcrumbs items={[{ name: t('nav.home'), url: path('/') }, { name: t('seo.aboutTitle') }]} />

        {/* Official company description — imported, never retyped. */}
        <p className="mt-6 text-lg font-semibold leading-relaxed text-slate-900">{brandDescription(lang)}</p>

        <AnswerBlock question={faqs[0].q} answer={faqs[0].a} />
        <EntityFacts
          rows={[
            { k: t('seo.factTrades'), v: String(SEO_TRADES.length) },
            { k: t('seo.factCounties'), v: String(COUNTIES.length) },
            { k: t('seo.factPrice'), v: money(LEAD_PRICE) },
            { k: t('seo.factPost'), v: t('seo.factFree') },
          ]}
        />
        <Section title={t('seo.aboutMissionT')}><p>{t('seo.aboutMissionB')}</p></Section>
        <Section title={t('seo.aboutHowT')}><p>{t('seo.aboutHowB')}</p><TrustStrip /></Section>
        <Section title={t('seo.aboutPrivacyT')}><p>{t('seo.aboutPrivacyB')}</p></Section>

        {/* Business contact details — public, no login required. */}
        <Section title={t('seo.aboutContactT')}>
          <p>{t('seo.aboutContactB')}</p>
          <dl className="mt-4 grid gap-3 sm:grid-cols-2">
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <dt className="text-xs font-bold uppercase tracking-wide text-slate-400">{t('footer.emailUs')}</dt>
              <dd className="mt-1">
                <a href={`mailto:${BRAND.email}`} className="font-bold text-slate-900 underline hover:text-orange-600">
                  {BRAND.email}
                </a>
              </dd>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <dt className="text-xs font-bold uppercase tracking-wide text-slate-400">{t('footer.address')}</dt>
              <dd className="mt-1">
                <address className="not-italic font-bold leading-relaxed text-slate-900">
                  <span className="block">{BRAND.fullName}</span>
                  {ADDRESS_LINES.map((line) => (
                    <span key={line} className="block">
                      {line}
                    </span>
                  ))}
                </address>
              </dd>
            </div>

          </dl>
          <p className="mt-3">
            <Link to={path('/contact')} className="font-bold text-orange-600 underline hover:text-orange-700">
              {t('footer.contactUs')}
            </Link>
          </p>
        </Section>

        {/* Company + legal pages, from the shared TRUST_PAGES list. */}
        <LinkGrid
          title={t('seo.aboutTrustT')}
          links={TRUST_PAGES.map((p) => ({ label: t(p.key), to: path(p.to) }))}
          columns={2}
        />

        <FaqSection faqs={faqs} />
        <LinkGrid title={t('seo.exploreHub')} links={hubLinks(path, t)} columns={3} />
        <CtaBand />
      </PageWrap>
    </>
  );
};


/* ---------------------------------------------------------- MEMBERSHIPS */
export const MembershipsPage: React.FC = () => {
  const { lang, path, t } = useI18n();
  const faqs = platformFaqs(lang);
  return (
    <>
      <Seo
        title={lang === 'es' ? 'Membresías para Subcontratistas' : 'Subcontractor Memberships'}
        description={t('seo.memberMeta', { price: money(LEAD_PRICE) })}
        schema={[
          breadcrumbSchema([{ name: 'Home', url: `/${lang}` }, { name: 'Memberships', url: `/${lang}/memberships` }]),
          offerCatalogSchema(),
          faqSchema(faqs),
        ]}
      />
      <PageHero eyebrow={t('seo.memberEyebrow')} title={t('seo.memberTitle')} intro={t('seo.memberIntro')} />
      <PageWrap>
        <Breadcrumbs items={[{ name: t('nav.home'), url: path('/') }, { name: t('seo.memberTitle') }]} />
        <AnswerBlock question={faqs[1].q} answer={faqs[1].a} />
        <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-3">
          {PLAN_CODES.map((code) => (
            <div key={code} className="rounded-2xl border border-slate-200 bg-white p-6">
              <h2 className="text-sm font-black uppercase tracking-wide text-slate-500">{code}</h2>
              <p className="mt-2 text-3xl font-black text-slate-900">
                {money(PLAN_PRICES[code])}
                <span className="text-sm font-semibold text-slate-500">{planBillingSuffix(code, lang)}</span>
              </p>

              <p className="mt-2 text-sm text-slate-600">{t('seo.includedLeads', { n: PLAN_INCLUDED_LEADS[code] })}</p>
              <p className="mt-1 text-xs text-slate-500">
                {PLAN_TERM_MONTHS[code] > 1 ? t('seo.term12') : t('seo.termMonthly')}
              </p>
              <Link to={path('/pricing')} className="mt-4 inline-block rounded-lg bg-orange-500 px-4 py-2 text-sm font-bold text-white hover:bg-orange-600">
                {t('seo.viewPlans')}
              </Link>
            </div>
          ))}
        </div>
        <Section title={t('seo.memberWhatT')}><p>{t('seo.memberWhatB')}</p><TrustStrip /></Section>
        <FaqSection faqs={faqs} />
        <LinkGrid title={t('seo.exploreHub')} links={hubLinks(path, t)} columns={3} />
        <CtaBand />
      </PageWrap>
    </>
  );
};

/* ------------------------------------------------------- AUDIENCE PAGES */
/**
 * One template, four audiences. "commercial" was added so commercial property
 * owners, property managers and businesses have a real landing page (and a real
 * indexable URL) instead of being folded into the homeowner page.
 */
export const AudiencePage: React.FC<{ who: 'gc' | 'sub' | 'homeowner' | 'commercial' }> = ({ who }) => {
  const { lang, path, t } = useI18n();
  const faqs = platformFaqs(lang);
  const key = who === 'gc' ? 'gc' : who === 'sub' ? 'sub' : who === 'commercial' ? 'comm' : 'ho';
  const slug =
    who === 'gc'
      ? 'general-contractors'
      : who === 'sub'
      ? 'subcontractors'
      : who === 'commercial'
      ? 'commercial'
      : 'homeowners';

  return (
    <>
      <Seo
        title={t(`seo.${key}Title`)}
        description={t(`seo.${key}Meta`)}
        schema={[
          breadcrumbSchema([{ name: 'Home', url: `/${lang}` }, { name: t(`seo.${key}Title`), url: `/${lang}/${slug}` }]),
          faqSchema(faqs),
        ]}
      />
      <PageHero eyebrow={t(`seo.${key}Eyebrow`)} title={t(`seo.${key}Title`)} intro={t(`seo.${key}Intro`)}>
        <Link to={path('/register')} className="rounded-xl bg-orange-500 px-5 py-3 text-sm font-bold hover:bg-orange-600">
          {t(`seo.${key}Cta`)}
        </Link>
        <Link to={path('/jobs')} className="rounded-xl border border-white/20 px-5 py-3 text-sm font-bold hover:bg-white/10">
          {t('seo.ctaBrowse')}
        </Link>
      </PageHero>
      <PageWrap>
        <Breadcrumbs items={[{ name: t('nav.home'), url: path('/') }, { name: t(`seo.${key}Title`) }]} />
        <AnswerBlock question={t(`seo.${key}Q`)} answer={t(`seo.${key}A`)} />
        <Section title={t('seo.howWorks')}>
          <ol className="space-y-3">
            <li><strong>1.</strong> {t(`seo.${key}Step1`)}</li>
            <li><strong>2.</strong> {t(`seo.${key}Step2`)}</li>
            <li><strong>3.</strong> {t(`seo.${key}Step3`)}</li>
          </ol>
          <TrustStrip />
        </Section>
        <FaqSection faqs={faqs} />
        <LinkGrid
          title={t('seo.allTrades')}
          icon="trade"
          links={SEO_TRADES.slice(0, 24).map((x) => ({ label: lang === 'es' ? x.es : x.en, to: path(`/trades/${x.slug}`) }))}
        />
        <LinkGrid title={t('seo.exploreHub')} links={hubLinks(path, t)} columns={3} />
        <CtaBand variant={who === 'sub' ? 'sub' : 'post'} />
      </PageWrap>
    </>
  );
};

/* -------------------------------------------------------- POST A PROJECT */
export const PostProjectPage: React.FC = () => {
  const { lang, path, t } = useI18n();
  const faqs = platformFaqs(lang);
  return (
    <>
      <Seo
        title={t('seo.postTitle')}
        description={t('seo.postMeta')}
        schema={[
          breadcrumbSchema([{ name: 'Home', url: `/${lang}` }, { name: 'Post a Project', url: `/${lang}/post-a-project` }]),
          faqSchema(faqs),
        ]}
      />
      <PageHero eyebrow={t('seo.postEyebrow')} title={t('seo.postTitle')} intro={t('seo.postIntro')}>
        <Link to={path('/register')} className="rounded-xl bg-orange-500 px-5 py-3 text-sm font-bold hover:bg-orange-600">
          {t('seo.ctaPostBtn')}
        </Link>
      </PageHero>
      <PageWrap>
        <Breadcrumbs items={[{ name: t('nav.home'), url: path('/') }, { name: t('seo.postTitle') }]} />
        <AnswerBlock question={faqs[3].q} answer={faqs[3].a} />
        <Section title={t('seo.howWorks')}>
          <ol className="space-y-3">
            <li><strong>1.</strong> {t('seo.postStep1')}</li>
            <li><strong>2.</strong> {t('seo.postStep2')}</li>
            <li><strong>3.</strong> {t('seo.postStep3')}</li>
          </ol>
          <TrustStrip />
        </Section>
        <FaqSection faqs={faqs} />
        <LinkGrid
          title={t('seo.allTrades')}
          icon="trade"
          links={SEO_TRADES.slice(0, 24).map((x) => ({ label: lang === 'es' ? x.es : x.en, to: path(`/trades/${x.slug}`) }))}
        />
        <CtaBand variant="post" />
      </PageWrap>
    </>
  );
};

/* ------------------------------------------------------------ FAQ / HELP */
export const FaqPage: React.FC<{ help?: boolean }> = ({ help }) => {
  const { lang, path, t } = useI18n();
  const faqs = platformFaqs(lang);
  const guides = RESOURCES.slice(0, 12).map((r) => ({
    label: lang === 'es' ? r.titleEs : r.title,
    to: path(`/resources/${r.slug}`),
  }));
  const title = help ? t('seo.helpTitle') : t('seo.faqTitle');
  return (
    <>
      <Seo
        title={title}
        description={help ? t('seo.helpMeta') : t('seo.faqMeta')}
        schema={[
          breadcrumbSchema([{ name: 'Home', url: `/${lang}` }, { name: title, url: `/${lang}/${help ? 'help' : 'faq'}` }]),
          faqSchema(faqs),
          itemListSchema('Help guides', guides.map((g) => ({ name: g.label, url: g.to }))),
        ]}
      />
      <PageHero eyebrow={t('seo.faqEyebrow')} title={title} intro={help ? t('seo.helpIntro') : t('seo.faqIntro')} />
      <PageWrap>
        <Breadcrumbs items={[{ name: t('nav.home'), url: path('/') }, { name: title }]} />
        <FaqSection faqs={faqs} title={t('seo.commonQuestions')} />
        <LinkGrid title={t('seo.relatedGuides')} links={guides} columns={2} />
        <LinkGrid title={t('seo.exploreHub')} links={hubLinks(path, t)} columns={3} />
        <CtaBand />
      </PageWrap>
    </>
  );
};
